import { formatDateDDMMYYYY } from '@/utils/textFormatting';
import { getResultTextColor, getResultLabel, shouldShowResult } from '@/utils/checkResultColors';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Search, Filter, Clock, CheckCircle, AlertTriangle, RefreshCw,
  FileText, Eye, Users, Building2, Calendar, TrendingUp, TrendingDown,
  BarChart3, ClipboardList, LogOut, Play, FileCheck, Phone, Mail, MapPin,
  Briefcase, GraduationCap, Gavel, Shield, Database, TestTube, UserCheck, ArrowRight,
  RotateCcw, Send, MoreVertical, PlusCircle, Timer, XCircle, CalendarClock, Share2, Copy, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import NotificationCenter from '@/components/shared/NotificationCenter';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import CompaniesTab from '@/components/admin/CompaniesTab';
import InstitutionsTab from '@/components/admin/InstitutionsTab';

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification', icon: MapPin },
  { value: 'employment_check', label: 'Employment Verification', icon: Briefcase },
  { value: 'education_check', label: 'Education Verification', icon: GraduationCap },
  { value: 'id_check', label: 'ID Verification', icon: Shield },
  { value: 'criminal_court_check', label: 'Criminal Check', icon: Gavel },
  { value: 'court_advocate_check', label: 'Court Advocate Check', icon: Gavel },
  { value: 'credit_check', label: 'Credit Check', icon: Database },
  { value: 'personal_reference', label: 'Personal Reference', icon: Users },
  { value: 'professional_reference', label: 'Professional Reference', icon: Users },
  { value: 'drug_test', label: 'Drug Test', icon: TestTube },
];

const MODE_OF_VERIFICATION = [
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'physical', label: 'Physical Visit', icon: MapPin },
  { value: 'database', label: 'Database Check', icon: Database },
  { value: 'document', label: 'Document Verification', icon: FileText },
  { value: 'video_call', label: 'Video Call', icon: Eye },
];

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-700',
  not_initiated: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  insufficiency: 'bg-red-100 text-red-800',
  // Legacy status mappings
  approved: 'bg-blue-100 text-blue-800',
  assigned: 'bg-purple-100 text-purple-800',
  vendor_assigned: 'bg-indigo-100 text-indigo-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  discrepant: 'bg-red-100 text-red-800',
  clear: 'bg-green-100 text-green-800',
  returned: 'bg-amber-100 text-amber-800',
};

const VERIFICATION_RESULTS = [
  { value: 'clear', label: 'Clear - No Issues' },
  { value: 'minor_discrepancy', label: 'Minor Discrepancy' },
  { value: 'major_discrepancy', label: 'Major Discrepancy' },
  { value: 'unable_to_verify', label: 'Unable to Verify' },
  { value: 'pending_response', label: 'Pending Response' },
];

const RETURN_REASONS = [
  { value: 'incorrect_assignment', label: 'Incorrect Assignment' },
  { value: 'missing_documents', label: 'Missing Documents' },
  { value: 'insufficient_data', label: 'Insufficient Data' },
  { value: 'out_of_scope', label: 'Out of Scope' },
  { value: 'requires_vendor', label: 'Requires External Vendor' },
  { value: 'duplicate_check', label: 'Duplicate Check' },
  { value: 'other', label: 'Other' },
];

// Default TAT for check types (in days) - used as fallback only
const DEFAULT_CHECK_TAT = {
  address_check: 5,
  employment_check: 7,
  education_check: 7,
  id_check: 3,
  criminal_court_check: 10,
  court_advocate_check: 10,
  credit_check: 5,
  personal_reference: 5,
  professional_reference: 5,
  drug_test: 5,
};

export default function AgentInternalVendorDashboard({ user, onLogout, onSwitchAccount }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({});
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [insufficiencies, setInsufficiencies] = useState([]);
  const [myAssignment, setMyAssignment] = useState(null);
  const [checkTypesConfig, setCheckTypesConfig] = useState({});
  
  // Completed-tab independent filters (May 24, 2026) — admins want the
  // Completed tab to show all completed work regardless of the
  // Dashboard's Vendor filter. Keeping these isolated avoids
  // accidental cross-contamination between tabs.
  const [completedVendorFilter, setCompletedVendorFilter] = useState('all');
  const [completedDateFilterType, setCompletedDateFilterType] = useState('all');
  const [completedDateFrom, setCompletedDateFrom] = useState('');
  const [completedDateTo, setCompletedDateTo] = useState('');

  const applyCompletedDateFilter = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setCompletedDateFilterType(filterType);
    if (filterType === 'today') {
      const d = today.toISOString().split('T')[0];
      setCompletedDateFrom(d); setCompletedDateTo(d);
    } else if (filterType === 'this_week') {
      const ws = new Date(today); ws.setDate(today.getDate() - today.getDay());
      setCompletedDateFrom(ws.toISOString().split('T')[0]);
      setCompletedDateTo(today.toISOString().split('T')[0]);
    } else if (filterType === 'this_month') {
      const ms = new Date(today.getFullYear(), today.getMonth(), 1);
      setCompletedDateFrom(ms.toISOString().split('T')[0]);
      setCompletedDateTo(today.toISOString().split('T')[0]);
    } else if (filterType === 'last_month') {
      const lms = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lme = new Date(today.getFullYear(), today.getMonth(), 0);
      setCompletedDateFrom(lms.toISOString().split('T')[0]);
      setCompletedDateTo(lme.toISOString().split('T')[0]);
    } else {
      setCompletedDateFrom(''); setCompletedDateTo('');
    }
  };

  // Filter completedChecks by the tab-local Vendor + Date filters.
  // Defined after `completedChecks` so it can reference it; moved
  // below the `completedChecks` const at render time.

  
  // Filters
  const [filters, setFilters] = useState({
    clientId: 'all',
    checkType: 'all',
    status: 'all',
    tatStatus: 'all',
    modeOfVerification: 'all',
    dateFilterType: 'latest',
    assignedFrom: '',
    assignedTo: '',
    initiatedFrom: '',
    initiatedTo: '',
    completedFrom: '',
    completedTo: '',
    search: '',
  });
  
  // Update check dialog
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    mode_of_verification: '',
    remarks: '',
    verification_result: '',
    verifier_notes: ''
  });

  // Report generation
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Return to Operations Manager
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnData, setReturnData] = useState({ reason: '', notes: '' });

  // Check details dialog
  const [showCheckDetails, setShowCheckDetails] = useState(false);

  // Bulk selection for Internal Team
  const [selectedChecks, setSelectedChecks] = useState([]);
  const [showBulkTransferDialog, setShowBulkTransferDialog] = useState(false);
  const [transferData, setTransferData] = useState({ vendor_id: '', reason: 'physical_visit_required', special_instructions: '' });
  // Manual urgent TAT reminder (May 22, 2026) — sent from per-row or bulk
  // action. Server groups by vendor and renders the same table as the
  // daily-scheduled mail with a red "URGENT / PENALTY" banner.
  const [showUrgentReminderDialog, setShowUrgentReminderDialog] = useState(false);
  const [urgentReminderSingleCheck, setUrgentReminderSingleCheck] = useState(null); // null = bulk
  const [urgentReminderSending, setUrgentReminderSending] = useState(false);
  const [urgentReminderResult, setUrgentReminderResult] = useState(null);

  // Determine team type based on user role
  const isInternalTeam = user?.role === 'inhouse_verification' || user?.team_type === 'internal';
  const isVendorTeam = user?.role === 'vendor_management' || user?.team_type === 'vendor';

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
    { id: 'my-checks', label: 'My Assigned Checks', icon: FileCheck, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
    { id: 'all-checks', label: 'All Checks', icon: FileText, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
    { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700' },
    { id: 'companies', label: 'Company Database', icon: Building2, color: 'indigo', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' },
    { id: 'institutions', label: 'Universities', icon: GraduationCap, color: 'teal', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
  ];

  useEffect(() => {
    fetchCheckTypesConfig();
    fetchData();
    fetchMyAssignment();
    fetchVendors();
    fetchClients();
    fetchInsufficiencies();
  }, []);

  // Re-enrich checks when check types config loads
  useEffect(() => {
    if (Object.keys(checkTypesConfig).length > 0) {
      fetchData();
    }
  }, [checkTypesConfig]);

  const fetchCheckTypesConfig = async () => {
    try {
      const res = await axios.get('/check-types');
      const configMap = {};
      (res.data || []).forEach(ct => {
        configMap[ct.id] = { default_tat: ct.default_tat || 7, agent_tat_buffer: ct.agent_tat_buffer ?? 2 };
      });
      setCheckTypesConfig(configMap);
    } catch (error) {
      console.log('Failed to fetch check types config');
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get('/agent/vendors');
      setVendors(res.data || []);
    } catch (error) {
      console.log('Failed to fetch vendors');
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get('/clients');
      setClients(res.data || []);
    } catch (error) {
      console.log('Failed to fetch clients');
    }
  };

  const fetchInsufficiencies = async () => {
    try {
      const res = await axios.get('/insufficiencies');
      setInsufficiencies(res.data || []);
    } catch (error) {
      console.log('Failed to fetch insufficiencies');
    }
  };

  const fetchMyAssignment = async () => {
    try {
      const res = await axios.get('/agent-assignments/me');
      setMyAssignment(res.data);
    } catch (error) {
      console.log('No assignment found');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.checkType && filters.checkType !== 'all') params.append('check_type', filters.checkType);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.tatStatus && filters.tatStatus !== 'all') params.append('tat_status', filters.tatStatus);
      if (filters.clientId && filters.clientId !== 'all') params.append('client_id', filters.clientId);
      if (filters.search) params.append('search', filters.search);
      if (filters.assignedFrom) params.append('assigned_from', filters.assignedFrom);
      if (filters.assignedTo) params.append('assigned_to', filters.assignedTo);
      if (filters.initiatedFrom) params.append('initiated_from', filters.initiatedFrom);
      if (filters.initiatedTo) params.append('initiated_to', filters.initiatedTo);

      // Perf: defer completed cases unless user is on the Completed tab.
      // ~83% of records are 'completed' on heavy tenants — pushing this
      // filter into Mongo cuts initial load from ~15s to ~1-2s.
      // (May 24, 2026 perf fix.)
      if (activeTab === 'completed') {
        params.append('case_status', 'completed,report_approved');
      } else if (activeTab === 'dashboard' || activeTab === 'my-checks') {
        params.append('exclude_completed', 'true');
      }

      const [checksRes, statsRes] = await Promise.all([
        axios.get(`/team/checks?${params.toString()}`),
        axios.get('/agent/stats').catch(() => ({ data: {} }))
      ]);
      
      let allChecks = checksRes.data || [];
      
      // Enrich checks with TAT calculation
      allChecks = allChecks.map(check => {
        const checkType = getCheckTypeString(check.check_type);
        const ctConfig = checkTypesConfig[checkType] || {};
        const clientTat = check.tat_days || ctConfig.default_tat || DEFAULT_CHECK_TAT[checkType] || 7;
        const buffer = ctConfig.agent_tat_buffer ?? 2;
        const agentTat = Math.max(clientTat - buffer, 1); // Agent TAT = Client TAT - buffer, minimum 1

        let daysElapsed = 0;
        let isWithinTat = true;
        const isPaused = !!check.tat_paused_at && !check.completed_at;

        if (check.assigned_at) {
          const assignedDate = new Date(check.assigned_at);
          // End cap: completion date, or pause moment if currently paused, else now
          let endDate;
          if (check.completed_at) {
            endDate = new Date(check.completed_at);
          } else if (isPaused) {
            endDate = new Date(check.tat_paused_at);
          } else {
            endDate = new Date();
          }
          const rawDays = (endDate - assignedDate) / (1000 * 60 * 60 * 24);
          // Subtract any previously accumulated pause duration (cleared insufficiencies)
          const pauseDays = Number(check.total_tat_paused_days) || 0;
          daysElapsed = Math.max(0, Math.ceil(rawDays - pauseDays));

          isWithinTat = daysElapsed <= agentTat;
        }

        return {
          ...check,
          client_tat: clientTat,
          agent_tat: agentTat,
          days_elapsed: daysElapsed,
          is_within_tat: isWithinTat,
          is_tat_paused: isPaused,
        };
      });

      setChecks(allChecks);
      setStats(statsRes.data || {});
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, checkTypesConfig, activeTab]);

  // Re-fetch when the user switches tabs so completed-list / active-list
  // payloads are scoped correctly (May 24, 2026 perf fix).
  useEffect(() => {
    if (Object.keys(checkTypesConfig).length > 0) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Helper functions
  const getCheckTypeString = (checkType) => {
    if (typeof checkType === 'string') return checkType;
    if (checkType && typeof checkType === 'object') return checkType.check_type || 'unknown';
    return 'unknown';
  };

  const getCheckTypeLabel = (checkType) => {
    const typeStr = getCheckTypeString(checkType);
    const found = CHECK_TYPES.find(ct => ct.value === typeStr);
    return found ? found.label : typeStr.replace(/_/g, ' ');
  };

  const getCheckTypeIcon = (checkType) => {
    const typeStr = getCheckTypeString(checkType);
    const found = CHECK_TYPES.find(ct => ct.value === typeStr);
    return found ? found.icon : FileText;
  };

  const getModeIcon = (mode) => {
    const found = MODE_OF_VERIFICATION.find(m => m.value === mode);
    return found ? found.icon : FileText;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  // Generate formatted text for field agent (copy to WhatsApp/SMS)
  const generateFieldAgentText = (check) => {
    const checkType = check.check_type?.toLowerCase() || '';
    const baseUrl = window.location.origin;
    const formLink = `${baseUrl}/field-verification/${check.case_id}/${check.check_id}`;
    
    let text = '';
    
    if (checkType.includes('address')) {
      text = `━━━━━━━━━━━━━━━━━━━━
ADDRESS VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || '-'}
JOINING COMPANY: ${check.client_name || '-'}
ADDRESS TYPE: ${check.address_type || 'Present'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || '-'}
ADDRESS: ${check.address || check.verification_address || '-'}
CITY: ${check.city || '-'}
LANDMARK: ${check.landmark || '-'}
STATE: ${check.state || '-'}
PINCODE: ${check.pincode || '-'}
CONTACT: ${check.phone || check.employee_phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.remarks || '-'}${check.special_instructions ? `\n\n⚠️ SPECIAL INSTRUCTIONS: ${check.special_instructions}` : ''}

📋 VERIFICATION FORM:
${formLink}`;
    } else if (checkType.includes('employment')) {
      text = `━━━━━━━━━━━━━━━━━━━━
EMPLOYMENT VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || '-'}
EMPLOYMENT TYPE: ${check.employment_type || 'Current'}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || '-'}
COMPANY TO VISIT: ${check.company_name || '-'}
ADDRESS: ${check.company_address || check.address || '-'}
CITY: ${check.company_city || check.city || '-'}
LANDMARK: ${check.landmark || '-'}
STATE: ${check.company_state || check.state || '-'}
PINCODE: ${check.company_pincode || check.pincode || '-'}
CONTACT: ${check.hr_contact || check.phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.remarks || '-'}${check.special_instructions ? `\n\n⚠️ SPECIAL INSTRUCTIONS: ${check.special_instructions}` : ''}

📋 VERIFICATION FORM:
${formLink}`;
    } else if (checkType.includes('education')) {
      text = `━━━━━━━━━━━━━━━━━━━━
EDUCATION VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || '-'}
EDUCATION TYPE: ${check.education_type || 'Highest'}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || '-'}
INSTITUTION: ${check.institution_name || '-'}
UNIVERSITY: ${check.university_name || '-'}
ADDRESS: ${check.institution_address || check.address || '-'}
CITY: ${check.institution_city || check.city || '-'}
LANDMARK: ${check.landmark || '-'}
STATE: ${check.institution_state || check.state || '-'}
PINCODE: ${check.institution_pincode || check.pincode || '-'}
CONTACT: ${check.registrar_contact || check.phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.remarks || '-'}${check.special_instructions ? `\n\n⚠️ SPECIAL INSTRUCTIONS: ${check.special_instructions}` : ''}

📋 VERIFICATION FORM:
${formLink}`;
    } else {
      text = `━━━━━━━━━━━━━━━━━━━━
FIELD VERIFICATION
━━━━━━━━━━━━━━━━━━━━
CHECK ID: ${check.case_id || '-'}
SUB-CHECK ID: ${check.check_id || '-'}
CHECK TYPE: ${check.check_type?.replace(/_/g, ' ').toUpperCase() || '-'}
JOINING COMPANY: ${check.client_name || '-'}
EMPLOYEE: ${check.employee_name || '-'}
FATHER NAME: ${check.father_name || '-'}
ADDRESS: ${check.address || check.verification_address || '-'}
CITY: ${check.city || '-'}
STATE: ${check.state || '-'}
PINCODE: ${check.pincode || '-'}
CONTACT: ${check.phone || check.employee_phone || '-'}
ALT CONTACT: ${check.alternate_contact || '-'}
REMARKS: ${check.remarks || '-'}${check.special_instructions ? `\n\n⚠️ SPECIAL INSTRUCTIONS: ${check.special_instructions}` : ''}

📋 VERIFICATION FORM:
${formLink}`;
    }
    
    return text;
  };

  const copyForFieldAgent = async (check) => {
    try {
      const text = generateFieldAgentText(check);
      await navigator.clipboard.writeText(text);
      toast.success('Copied! Share via WhatsApp or SMS');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Computed data
  // Match both current and original agent ownership (agent_assigned_to is
  // preserved when a vendor subsequently overwrites assigned_to).
  const myChecks = checks.filter(c => c.assigned_to === user.id || c.agent_assigned_to === user.id);
  const unassignedChecks = checks.filter(c => !c.assigned_to && (c.status === 'approved' || c.status === 'pending'));
  const newlyAddedChecks = checks.filter(c => {
    if (!c.created_at) return false;
    const created = new Date(c.created_at);
    const now = new Date();
    const hoursDiff = (now - created) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  });
  const withinTatChecks = myChecks.filter(c => c.is_within_tat && c.status !== 'verified' && c.status !== 'clear');
  const overTatChecks = myChecks.filter(c => !c.is_within_tat && c.status !== 'verified' && c.status !== 'clear');
  const insuffRaised = insufficiencies.filter(i => i.status === 'open');
  const completedChecks = myChecks.filter(c => c.status === 'verified' || c.status === 'clear' || c.status === 'failed');

  // Filter completedChecks by the tab-local Vendor + Date filters
  // (May 24, 2026). useMemo MUST be placed after the `completedChecks`
  // const because JS const declarations sit in the Temporal Dead Zone
  // until execution reaches them.
  const completedChecksFiltered = React.useMemo(() => {
    let list = completedChecks;
    if (completedVendorFilter !== 'all') {
      list = list.filter(c => {
        const vid = c.assigned_vendor_id || c.vendor_id || (c.assigned_vendor && c.assigned_vendor.vendor_id);
        return vid === completedVendorFilter;
      });
    }
    if (completedDateFrom) {
      list = list.filter(c => {
        const d = (c.completed_at || c.verified_at || '').slice(0, 10);
        return d && d >= completedDateFrom;
      });
    }
    if (completedDateTo) {
      list = list.filter(c => {
        const d = (c.completed_at || c.verified_at || '').slice(0, 10);
        return d && d <= completedDateTo;
      });
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedChecks, completedVendorFilter, completedDateFrom, completedDateTo]);

  // Client-wise TAT summary
  const clientTatSummary = {};
  myChecks.forEach(check => {
    const clientName = check.client_name || 'Unknown';
    if (!clientTatSummary[clientName]) {
      clientTatSummary[clientName] = { total: 0, within_tat: 0, over_tat: 0, check_types: {} };
    }
    clientTatSummary[clientName].total++;
    if (check.is_within_tat) {
      clientTatSummary[clientName].within_tat++;
    } else {
      clientTatSummary[clientName].over_tat++;
    }
    
    // Check-wise breakdown
    const checkType = getCheckTypeString(check.check_type);
    if (!clientTatSummary[clientName].check_types[checkType]) {
      clientTatSummary[clientName].check_types[checkType] = { total: 0, within_tat: 0, over_tat: 0 };
    }
    clientTatSummary[clientName].check_types[checkType].total++;
    if (check.is_within_tat) {
      clientTatSummary[clientName].check_types[checkType].within_tat++;
    } else {
      clientTatSummary[clientName].check_types[checkType].over_tat++;
    }
  });

  // Drill-down data
  const getDrillDownData = () => {
    switch (drillDownType) {
      case 'newly_added': return { title: 'Newly Added (24h)', color: 'blue', data: newlyAddedChecks };
      case 'unassigned': return { title: 'Unassigned Checks', color: 'gray', data: unassignedChecks };
      case 'within_tat': return { title: 'Within TAT', color: 'green', data: withinTatChecks };
      case 'over_tat': return { title: 'Over TAT', color: 'red', data: overTatChecks };
      case 'insuff_raised': return { title: 'Insufficiencies Raised', color: 'amber', data: insuffRaised };
      case 'completed': return { title: 'Completed', color: 'emerald', data: completedChecks };
      default: return { title: '', color: 'gray', data: [] };
    }
  };

  const handleStatCardClick = (type) => {
    setDrillDownType(type);
    setShowDrillDown(true);
  };

  // Dialog handlers
  const openUpdateStatus = (check) => {
    setSelectedCheck(check);
    setStatusUpdate({
      status: check.status || '',
      mode_of_verification: check.mode_of_verification || '',
      remarks: check.remarks || '',
      verification_result: check.verification_result || '',
      verifier_notes: check.verifier_notes || ''
    });
    setShowUpdateStatus(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedCheck || !statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }
    try {
      await axios.put(`/team/checks/${selectedCheck.case_id}/${selectedCheck.check_id || selectedCheck.id}/status`, {
        ...statusUpdate,
        mode_of_verification: statusUpdate.mode_of_verification
      });
      toast.success('Check status updated');
      setShowUpdateStatus(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openGenerateReport = (check) => {
    setSelectedCheck(check);
    setReportData({
      check_type: getCheckTypeLabel(check.check_type),
      employee_name: check.employee_name,
      status: check.status,
      mode_of_verification: check.mode_of_verification,
      verification_result: check.verification_result,
      remarks: check.remarks,
      verified_at: check.completed_at || check.updated_at
    });
    setShowGenerateReport(true);
  };

  const generateReport = async () => {
    try {
      toast.info('Generating BGV Report...');
      const response = await axios.post(`/cases/${selectedCheck.case_id}/generate-bgv-report`, {
        is_final: false
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `BGV_Report_${selectedCheck.case_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
      setShowGenerateReport(false);
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const openReturnDialog = (check) => {
    setSelectedCheck(check);
    setReturnData({ reason: '', notes: '' });
    setShowReturnDialog(true);
  };

  const handleReturnCheck = async () => {
    if (!selectedCheck || !returnData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/agent/checks/${selectedCheck.case_id}/${selectedCheck.check_id || selectedCheck.id}/return`, returnData);
      toast.success('Check returned to Operations Manager');
      setShowReturnDialog(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to return check');
    }
  };

  // Bulk transfer to Vendor Team (for Internal Team - Employment/Education physical visits)
  const handleBulkTransferToVendor = async () => {
    if (selectedChecks.length === 0) {
      toast.error('No checks selected');
      return;
    }
    if (!transferData.vendor_id) {
      toast.error('Please select a vendor');
      return;
    }
    try {
      const promises = selectedChecks.map(checkId => {
        const check = checks.find(c => c.check_id === checkId || c.id === checkId);
        if (check) {
          return axios.post('/operations/reassign-check', {
            case_id: check.case_id,
            check_id: check.check_id || check.id,
            target_type: 'vendor',
            target_id: transferData.vendor_id,
            reason: `Transfer for physical visit: ${transferData.reason}`,
            special_instructions: transferData.special_instructions
          });
        }
        return Promise.resolve();
      });
      
      await Promise.all(promises);
      toast.success(`${selectedChecks.length} checks transferred to Vendor Team`);
      setShowBulkTransferDialog(false);
      setSelectedChecks([]);
      setTransferData({ vendor_id: '', reason: 'physical_visit_required', special_instructions: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to transfer checks');
    }
  };

  // Urgent TAT-reminder send. `singleCheck` is the row when invoked per-row;
  // when null, the bulk-selection (`selectedChecks`) drives the payload.
  const sendUrgentReminder = async (singleCheck = null) => {
    const targets = singleCheck
      ? [{ case_id: singleCheck.case_id, check_id: singleCheck.check_id }]
      : selectedChecks
          .map((cid) => {
            const row = recentChecks.find((rc) => rc.check_id === cid) || verificationData.find((vd) => vd.check_id === cid);
            return row ? { case_id: row.case_id, check_id: row.check_id } : null;
          })
          .filter(Boolean);
    if (targets.length === 0) {
      toast.error('No checks selected');
      return;
    }
    setUrgentReminderSending(true);
    setUrgentReminderResult(null);
    try {
      const res = await axios.post('/vendor/send-urgent-tat-reminder', { check_assignments: targets });
      setUrgentReminderResult(res.data);
      toast.success(res.data.message || 'Urgent reminder sent');
      if (!singleCheck) {
        setSelectedChecks([]);
      }
      setTimeout(() => {
        setShowUrgentReminderDialog(false);
        setUrgentReminderSingleCheck(null);
        setUrgentReminderResult(null);
      }, 2200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send urgent reminder');
    } finally {
      setUrgentReminderSending(false);
    }
  };

  // Toggle check selection
  const toggleCheckSelection = (checkId) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId)
        : [...prev, checkId]
    );
  };

  // Select all visible checks
  const toggleSelectAll = (checksToSelect) => {
    const checkIds = checksToSelect.map(c => c.check_id || c.id);
    const allSelected = checkIds.every(id => selectedChecks.includes(id));
    
    if (allSelected) {
      setSelectedChecks(prev => prev.filter(id => !checkIds.includes(id)));
    } else {
      setSelectedChecks(prev => [...new Set([...prev, ...checkIds])]);
    }
  };

  const openCheckDetails = (check) => {
    setSelectedCheck(check);
    setShowCheckDetails(true);
  };

  // Quick date filters
  const applyDateFilter = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let newFilters = { ...filters, dateFilterType: filterType };
    
    switch (filterType) {
      case 'today':
        newFilters.assignedFrom = today.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        newFilters.assignedFrom = weekStart.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        newFilters.assignedFrom = monthStart.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      case 'last_month': {
        // Last calendar month — from 1st of previous month to last day
        // of previous month (May 24, 2026).
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        newFilters.assignedFrom = lastMonthStart.toISOString().split('T')[0];
        newFilters.assignedTo = lastMonthEnd.toISOString().split('T')[0];
        break;
      }
      default:
        newFilters.assignedFrom = '';
        newFilters.assignedTo = '';
    }
    
    setFilters(newFilters);
  };

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card 
          className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('newly_added')}
          data-testid="stat-newly-added"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <PlusCircle className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-900">{newlyAddedChecks.length}</p>
                <p className="text-xs text-blue-700 font-medium">Newly Added</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('unassigned')}
          data-testid="stat-unassigned"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-slate-600" />
              <div>
                <p className="text-2xl font-bold text-slate-900">{unassignedChecks.length}</p>
                <p className="text-xs text-slate-700 font-medium">Unassigned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('within_tat')}
          data-testid="stat-within-tat"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Timer className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-900">{withinTatChecks.length}</p>
                <p className="text-xs text-green-700 font-medium">Within TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-red-200 bg-gradient-to-br from-red-50 to-red-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('over_tat')}
          data-testid="stat-over-tat"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-900">{overTatChecks.length}</p>
                <p className="text-xs text-red-700 font-medium">Over TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('insuff_raised')}
          data-testid="stat-insuff"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-900">{insuffRaised.length}</p>
                <p className="text-xs text-amber-700 font-medium">Insuff Raised</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100 cursor-pointer hover:shadow-lg transition-all"
          onClick={() => handleStatCardClick('completed')}
          data-testid="stat-completed"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-900">{completedChecks.length}</p>
                <p className="text-xs text-emerald-700 font-medium">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client-wise TAT Summary */}
      {Object.keys(clientTatSummary).length > 0 && (
        <Card className="border-indigo-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Client-wise Check TAT Summary
              <Badge variant="outline" className="ml-2 text-xs">Agent TAT = Client TAT - 2 days</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-indigo-50">
                    <TableHead>Client</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center text-green-700">Within TAT</TableHead>
                    <TableHead className="text-center text-red-700">Over TAT</TableHead>
                    <TableHead>Check-wise Breakdown</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(clientTatSummary).map(([client, data]) => (
                    <TableRow key={client}>
                      <TableCell className="font-medium">{client}</TableCell>
                      <TableCell className="text-center">{data.total}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-green-100 text-green-800">{data.within_tat}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-red-100 text-red-800">{data.over_tat}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(data.check_types).map(([type, counts]) => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {getCheckTypeLabel(type)}: {counts.within_tat}/{counts.total}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <Label className="text-sm font-medium mr-2">Quick Filters:</Label>
            {['latest', 'today', 'this_week', 'this_month', 'last_month'].map(type => (
              <Button 
                key={type}
                size="sm" 
                variant={filters.dateFilterType === type ? 'default' : 'outline'}
                onClick={() => applyDateFilter(type)}
                className={filters.dateFilterType === type ? 'bg-indigo-600' : ''}
              >
                {type === 'latest' && <RefreshCw className="w-3 h-3 mr-1" />}
                {type === 'today' && <Calendar className="w-3 h-3 mr-1" />}
                {type === 'this_week' && <CalendarClock className="w-3 h-3 mr-1" />}
                {type === 'this_month' && <BarChart3 className="w-3 h-3 mr-1" />}
                {type === 'last_month' && <BarChart3 className="w-3 h-3 mr-1" />}
                {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
              <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
              <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CHECK_TYPES.map(ct => (
                  <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="insufficiency">Insufficiency</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
              <SelectTrigger><SelectValue placeholder="TAT Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All TAT</SelectItem>
                <SelectItem value="within_tat">Within TAT</SelectItem>
                <SelectItem value="over_tat">Over TAT</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search..." 
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
              />
            </div>

            <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700">
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* My Checks Table */}
      {renderChecksTable(myChecks.slice(0, 15), 'My Assigned Checks', true)}
    </div>
  );

  // Render Checks Table (QC-style with all dates and TAT)
  const renderChecksTable = (checksToShow, title, showActions = true) => (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5" />
            {title}
            <Badge variant="outline" className="ml-2">{checksToShow.length}</Badge>
          </CardTitle>
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[100px]">Case ID</TableHead>
                <TableHead className="w-[80px]">Check ID</TableHead>
                <TableHead>Employee</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Check Type</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">
                  <div className="text-xs">Field Status</div>
                  <div className="text-[10px] text-gray-500">Agent Remarks</div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="text-xs">Initiated</div>
                  <div className="text-[10px] text-gray-500">Case Created</div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="text-xs">Assigned/Sent</div>
                  <div className="text-[10px] text-gray-500">To Vendor/Mails</div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="text-xs">Completed</div>
                  <div className="text-[10px] text-gray-500">Verified</div>
                </TableHead>
                <TableHead className="text-center">
                  <div className="text-xs">TAT</div>
                  <div className="text-[10px] text-gray-500">Days/Limit</div>
                </TableHead>
                {showActions && <TableHead className="w-[150px]">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={showActions ? 13 : 12} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : checksToShow.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showActions ? 13 : 12} className="text-center py-8 text-gray-500">
                    No checks found
                  </TableCell>
                </TableRow>
              ) : checksToShow.map((check, idx) => {
                const ModeIcon = getModeIcon(check.mode_of_verification);
                const CheckIcon = getCheckTypeIcon(check.check_type);
                const isOverTat = !check.is_within_tat && check.status !== 'verified' && check.status !== 'clear';
                
                // Determine the assignment date based on type
                const getAssignmentDateInfo = () => {
                  if (check.assignment_type === 'vendor' || check.status === 'vendor_assigned') {
                    return {
                      date: check.vendor_assigned_at || check.assigned_at,
                      label: 'To Vendor',
                      color: 'text-indigo-600'
                    };
                  } else if (check.mail_sent_at || check.mails_sent_at) {
                    return {
                      date: check.mail_sent_at || check.mails_sent_at,
                      label: 'Mails Sent',
                      color: 'text-blue-600'
                    };
                  } else if (check.assigned_at) {
                    return {
                      date: check.assigned_at,
                      label: 'Assigned',
                      color: 'text-purple-600'
                    };
                  }
                  return { date: null, label: '-', color: 'text-gray-400' };
                };
                
                const assignmentInfo = getAssignmentDateInfo();
                
                return (
                  <TableRow key={idx} className={`hover:bg-slate-50 ${isOverTat ? 'bg-red-50/30' : ''}`}>
                    <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                    <TableCell className="font-mono text-xs text-blue-600">{check.check_id?.slice(-8) || check.id?.slice(-8) || '-'}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{check.employee_name}</p>
                        <p className="text-xs text-gray-500">{check.employee_email?.slice(0, 20)}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{check.client_name || 'N/A'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <CheckIcon className="w-3 h-3 text-gray-500" />
                          <span className="text-xs">{getCheckTypeLabel(check.check_type)}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCheckDetails(check)}
                            className="h-5 w-5 p-0 ml-1"
                            title={(() => {
                              const ct = (check.check_type || '').toLowerCase();
                              const sd = check.subcheck_data || {};
                              if (ct.includes('employment')) {
                                const c = sd.company_name || check.subcheck_company || '';
                                return c ? `Company: ${c}` : 'View Details';
                              }
                              if (ct.includes('education')) {
                                const u = sd.university_name || sd.institution_name || check.subcheck_institution || '';
                                return u ? `University: ${u}` : 'View Details';
                              }
                              return 'View Details';
                            })()}
                            data-testid={`view-check-tooltip-${check.check_id || check.id}`}
                          >
                            <Eye className="w-3.5 h-3.5 text-blue-500 hover:text-blue-700" />
                          </Button>
                        </div>
                        {check.subcheck_type && (
                          <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded w-fit">{check.subcheck_type}</span>
                        )}
                        {check.permanent_same_as_present && check.sub_option === 'permanent' && (
                          <span data-testid={`same-as-present-badge-${check.check_id}`} className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200 w-fit">
                            Same as Present - Mirrored
                          </span>
                        )}
                        {check.synced_from_present && (
                          <span className="text-[9px] text-blue-400 italic">Auto-synced</span>
                        )}
                        {check.address_updated_after_unlink && (
                          <span data-testid={`address-updated-badge-${check.check_id}`} className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-300 w-fit font-semibold">
                            Updated address — re-verify
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {check.mode_of_verification ? (
                        <div className="flex items-center gap-1">
                          <ModeIcon className="w-3 h-3 text-gray-500" />
                          <span className="text-xs capitalize">{check.mode_of_verification.replace('_', ' ')}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge data-testid={`check-status-badge-${check.check_id || idx}`} className={`${STATUS_COLORS[check.status] || 'bg-gray-100'} text-xs`}>
                          {check.status?.replace('_', ' ')}
                        </Badge>
                        {(check.status === 'in_progress' || check.verification_progress === 'in_progress') && check.insufficiency_cleared_date && !check.has_insufficiency && (
                          <span data-testid={`check-insuff-cleared-${check.check_id || idx}`} className="text-[10px] font-semibold text-amber-700">
                            Insuff cleared
                          </span>
                        )}
                        {(check.status === 'completed' || check.status === 'verified') && shouldShowResult(check.verification_result) && (
                          <span data-testid={`check-result-label-${check.check_id || idx}`} className={`text-[10px] font-semibold ${getResultTextColor(check.verification_result)}`}>
                            {getResultLabel(check.verification_result)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-xs ${
                        check.field_verification_submitted || check.field_verification_status === 'completed' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : check.field_verification_status === 'in_progress' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {check.field_verification_submitted 
                          ? 'Submitted' 
                          : check.field_verification_status || 'Pending'}
                      </Badge>
                      {check.field_agent_remarks && (
                        <div className="text-[9px] text-gray-500 mt-1 max-w-[80px] truncate" title={check.field_agent_remarks}>
                          {check.field_agent_remarks}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-center text-gray-500">
                      {formatDate(check.created_at || check.initiated_at)}
                    </TableCell>
                    <TableCell className="text-xs text-center">
                      <div className={assignmentInfo.color}>
                        {formatDate(assignmentInfo.date)}
                      </div>
                      {assignmentInfo.date && (
                        <div className="text-[9px] text-gray-400">{assignmentInfo.label}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-center text-gray-500">
                      {formatDate(check.completed_at)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {check.days_elapsed || 0}/{check.agent_tat || 7}
                      </Badge>
                    </TableCell>
                    {showActions && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openUpdateStatus(check)}
                            className="text-xs h-7 px-2"
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Update
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => copyForFieldAgent(check)}
                            className="text-xs h-7 px-2 border-purple-200 text-purple-700"
                            title="Copy details for field agent (WhatsApp/SMS)"
                          >
                            <Share2 className="w-3 h-3" />
                          </Button>
                          {(check.status === 'verified' || check.status === 'clear' || check.status === 'failed') && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openGenerateReport(check)}
                              className="text-xs h-7 px-2 border-green-200 text-green-700"
                            >
                              <FileText className="w-3 h-3" />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => copyForFieldAgent(check)} className="text-purple-600 cursor-pointer">
                                <Share2 className="w-4 h-4 mr-2" />
                                Copy for Field Agent
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                const baseUrl = window.location.origin;
                                const link = `${baseUrl}/field-verification/${check.case_id}/${check.check_id}`;
                                window.open(link, '_blank');
                              }} className="text-blue-600 cursor-pointer">
                                <Upload className="w-4 h-4 mr-2" />
                                Open Field Form
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                data-testid={`row-urgent-reminder-${check.check_id}`}
                                onClick={() => { setUrgentReminderSingleCheck(check); setShowUrgentReminderDialog(true); setUrgentReminderResult(null); }}
                                className="text-red-600 cursor-pointer"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Send Urgent Reminder
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openReturnDialog(check)} className="text-amber-600 cursor-pointer">
                                <RotateCcw className="w-4 h-4 mr-2" />
                                Return to Ops Manager
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboard();
      case 'my-checks':
        return (
          <div className="space-y-4">
            {/* Filters for My Checks */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                  <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
                    <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {CHECK_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not_initiated">Not Initiated</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="insufficiency">Insufficiency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
                    <SelectTrigger><SelectValue placeholder="TAT" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All TAT</SelectItem>
                      <SelectItem value="within_tat">Within TAT</SelectItem>
                      <SelectItem value="over_tat">Over TAT</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={filters.assignedFrom} onChange={(e) => setFilters({...filters, assignedFrom: e.target.value})} placeholder="Assigned From" />
                  <Input type="date" value={filters.assignedTo} onChange={(e) => setFilters({...filters, assignedTo: e.target.value})} placeholder="Assigned To" />
                  <Button onClick={fetchData} className="bg-indigo-600"><Filter className="w-4 h-4 mr-2" />Apply</Button>
                </div>
              </CardContent>
            </Card>
            {renderChecksTable(myChecks, 'My Assigned Checks', true)}
          </div>
        );
      case 'all-checks':
        return (
          <div className="space-y-4">
            {/* Filters for All Checks */}
            <Card>
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
                  <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
                    <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Clients</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
                    <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {CHECK_TYPES.map(ct => (
                        <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not_initiated">Not Initiated</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="insufficiency">Insufficiency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
                    <SelectTrigger><SelectValue placeholder="TAT" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All TAT</SelectItem>
                      <SelectItem value="within_tat">Within TAT</SelectItem>
                      <SelectItem value="over_tat">Over TAT</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={filters.assignedFrom} onChange={(e) => setFilters({...filters, assignedFrom: e.target.value})} />
                  <Input type="date" value={filters.assignedTo} onChange={(e) => setFilters({...filters, assignedTo: e.target.value})} />
                  <Button onClick={fetchData} className="bg-indigo-600"><Filter className="w-4 h-4 mr-2" />Apply</Button>
                </div>
              </CardContent>
            </Card>
            {renderChecksTable(checks, 'All Checks', true)}
          </div>
        );
      case 'completed':
        return (
          <div className="space-y-4">
            {/* Completed-tab independent filters (May 24, 2026) */}
            <Card className="border-emerald-200" data-testid="completed-tab-filters">
              <CardContent className="pt-4">
                <div className="flex flex-wrap gap-2 mb-3 items-center">
                  <Label className="text-sm font-medium mr-2">Date:</Label>
                  {['all', 'today', 'this_week', 'this_month', 'last_month'].map(type => (
                    <Button
                      key={type}
                      size="sm"
                      variant={completedDateFilterType === type ? 'default' : 'outline'}
                      onClick={() => applyCompletedDateFilter(type)}
                      className={completedDateFilterType === type ? 'bg-emerald-600' : ''}
                      data-testid={`completed-date-${type}`}
                    >
                      {type === 'all' ? 'All Time' : type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <Label className="text-sm font-medium mr-2">Vendor:</Label>
                  <Select value={completedVendorFilter} onValueChange={setCompletedVendorFilter}>
                    <SelectTrigger className="w-[260px] h-9" data-testid="completed-vendor-filter">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(completedVendorFilter !== 'all' || completedDateFilterType !== 'all') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setCompletedVendorFilter('all'); applyCompletedDateFilter('all'); }}
                      className="text-slate-600"
                      data-testid="completed-clear-filters"
                    >
                      Clear
                    </Button>
                  )}
                  <span className="text-xs text-slate-500 ml-auto">
                    Showing {completedChecksFiltered.length} of {completedChecks.length}
                  </span>
                </div>
              </CardContent>
            </Card>
            {renderChecksTable(completedChecksFiltered, 'Completed Checks', true)}
          </div>
        );
      case 'companies':
        return <CompaniesTab />;
      case 'institutions':
        return <InstitutionsTab />;
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header */}
      <div className={`${isInternalTeam ? 'bg-gradient-to-r from-cyan-900 to-teal-900' : 'bg-gradient-to-r from-violet-900 to-purple-900'} text-white px-6 py-4 flex items-center justify-between shadow-lg`}>
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">
              {isInternalTeam ? 'Agent Internal Team' : 'Agent Vendor Team'}
            </h1>
            <p className={`${isInternalTeam ? 'text-cyan-200' : 'text-violet-200'} text-sm`}>
              {isInternalTeam 
                ? 'Employment, Education & Reference Verifications' 
                : 'Address, Criminal, ID & External Verifications'}
            </p>
          </div>
          <Badge variant="outline" className="text-white border-white/30 bg-white/10 px-3 py-1">
            {user?.name || 'Agent'}
          </Badge>
          {isInternalTeam && (
            <Badge className="bg-cyan-500/30 text-white border-cyan-300">Internal Team</Badge>
          )}
          {isVendorTeam && (
            <Badge className="bg-violet-500/30 text-white border-violet-300">Vendor Team</Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Bulk Transfer Button for Internal Team */}
          {isInternalTeam && selectedChecks.length > 0 && (
            <>
              <Button
                data-testid="bulk-urgent-reminder-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setShowUrgentReminderDialog(true)}
              >
                <Send className="w-4 h-4 mr-2" />
                Send Reminder ({selectedChecks.length})
              </Button>
              <Button
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => setShowBulkTransferDialog(true)}
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Transfer to Vendor ({selectedChecks.length})
              </Button>
            </>
          )}
          <div className="bg-white/10 rounded-full p-1">
            <NotificationCenter />
          </div>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={onLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b px-6 py-2 shadow-sm">
        <div className="flex gap-2">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`px-5 py-3 flex items-center gap-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                  isActive
                    ? `${item.bgColor} ${item.textColor} shadow-sm`
                    : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
                data-testid={`tab-${item.id}`}
              >
                <Icon className={`w-5 h-5 ${isActive ? item.textColor : 'text-gray-400'}`} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {renderContent()}
      </main>

      {/* Update Status Dialog */}
      <Dialog open={showUpdateStatus} onOpenChange={setShowUpdateStatus}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Check Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCheck && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Case ID:</span> {selectedCheck.case_id}</div>
                  <div><span className="text-slate-500">Check ID:</span> {selectedCheck.check_id?.slice(-8) || selectedCheck.id?.slice(-8)}</div>
                  <div><span className="text-slate-500">Type:</span> {getCheckTypeLabel(selectedCheck.check_type)}</div>
                  <div><span className="text-slate-500">Employee:</span> {selectedCheck.employee_name}</div>
                  <div><span className="text-slate-500">TAT:</span> {selectedCheck.days_elapsed || 0}/{selectedCheck.agent_tat || 7} days</div>
                </div>
              </div>
            )}

            <div>
              <Label>Status *</Label>
              <Select value={statusUpdate.status} onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="verified">Verified (Clear)</SelectItem>
                  <SelectItem value="failed">Failed (Discrepancy)</SelectItem>
                  <SelectItem value="unable_to_verify">Unable to Verify</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Mode of Verification *</Label>
              <Select value={statusUpdate.mode_of_verification} onValueChange={(v) => setStatusUpdate({...statusUpdate, mode_of_verification: v})}>
                <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                <SelectContent>
                  {MODE_OF_VERIFICATION.map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className="flex items-center gap-2">
                        <m.icon className="w-4 h-4" />
                        {m.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Verification Result</Label>
              <Select value={statusUpdate.verification_result} onValueChange={(v) => setStatusUpdate({...statusUpdate, verification_result: v})}>
                <SelectTrigger><SelectValue placeholder="Select result" /></SelectTrigger>
                <SelectContent>
                  {VERIFICATION_RESULTS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Remarks</Label>
              <Textarea 
                value={statusUpdate.remarks}
                onChange={(e) => setStatusUpdate({...statusUpdate, remarks: e.target.value})}
                placeholder="Add verification notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateStatus(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} className="bg-indigo-600 hover:bg-indigo-700">
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Report Dialog */}
      <Dialog open={showGenerateReport} onOpenChange={setShowGenerateReport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Verification Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {reportData && selectedCheck && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Check Type:</span><span className="font-medium">{reportData.check_type}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Employee:</span><span className="font-medium">{reportData.employee_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><Badge className={STATUS_COLORS[reportData.status]}>{reportData.status}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-500">Mode:</span><span className="font-medium capitalize">{reportData.mode_of_verification?.replace('_', ' ') || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Result:</span><span className="font-medium capitalize">{reportData.verification_result?.replace('_', ' ') || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">TAT:</span><span className="font-medium">{selectedCheck.days_elapsed || 0} days (Limit: {selectedCheck.agent_tat || 7})</span></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateReport(false)}>Cancel</Button>
            <Button onClick={generateReport} className="bg-green-600 hover:bg-green-700">
              <FileText className="w-4 h-4 mr-2" />
              Generate PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return to Operations Manager Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="w-5 h-5" />
              Return Check to Operations Manager
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCheck && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-amber-700">Case ID:</span> {selectedCheck.case_id}</div>
                  <div><span className="text-amber-700">Check ID:</span> {selectedCheck.check_id?.slice(-8) || selectedCheck.id?.slice(-8)}</div>
                  <div><span className="text-amber-700">Type:</span> {getCheckTypeLabel(selectedCheck.check_type)}</div>
                  <div><span className="text-amber-700">Employee:</span> {selectedCheck.employee_name}</div>
                </div>
              </div>
            )}
            <div>
              <Label>Reason for Return *</Label>
              <Select value={returnData.reason} onValueChange={(v) => setReturnData({...returnData, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  {RETURN_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea 
                value={returnData.notes}
                onChange={(e) => setReturnData({...returnData, notes: e.target.value})}
                placeholder="Provide any additional details..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
            <Button onClick={handleReturnCheck} className="bg-amber-600 hover:bg-amber-700">
              <RotateCcw className="w-4 h-4 mr-2" />
              Return Check
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Details Dialog */}
      <Dialog open={showCheckDetails} onOpenChange={setShowCheckDetails}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Check Details</DialogTitle>
          </DialogHeader>
          {selectedCheck && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Case Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-gray-500">Case ID:</span> {selectedCheck.case_id}</div>
                    <div><span className="text-gray-500">Check ID:</span> {selectedCheck.check_id || selectedCheck.id}</div>
                    <div><span className="text-gray-500">Check Type:</span> {getCheckTypeLabel(selectedCheck.check_type)}</div>
                    <div><span className="text-gray-500">Status:</span> <Badge className={STATUS_COLORS[selectedCheck.status]}>{selectedCheck.status}</Badge></div>
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Employee Information</h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-gray-500">Name:</span> {selectedCheck.employee_name}</div>
                    <div><span className="text-gray-500">Email:</span> {selectedCheck.employee_email}</div>
                    <div><span className="text-gray-500">Contact:</span> {selectedCheck.phone || '-'}</div>
                    {selectedCheck.alternate_contact && (
                      <div><span className="text-gray-500">Alt Contact:</span> {selectedCheck.alternate_contact}</div>
                    )}
                    <div><span className="text-gray-500">Client:</span> {selectedCheck.client_name}</div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold mb-2 text-sm">Timeline & TAT</h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div><span className="text-gray-500">Initiated:</span><br/>{formatDate(selectedCheck.created_at)}</div>
                  <div><span className="text-gray-500">Assigned:</span><br/>{formatDate(selectedCheck.assigned_at)}</div>
                  <div><span className="text-gray-500">Completed:</span><br/>{formatDate(selectedCheck.completed_at)}</div>
                  <div>
                    <span className="text-gray-500">TAT Status:</span><br/>
                    <Badge className={selectedCheck.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {selectedCheck.days_elapsed || 0}/{selectedCheck.agent_tat || 7} days
                    </Badge>
                  </div>
                </div>
              </div>
              {selectedCheck.remarks && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-sm">Remarks</h4>
                  <p className="text-sm">{selectedCheck.remarks}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down Dialog */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getDrillDownData().title}
              <Badge>{getDrillDownData().data.length}</Badge>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Case ID</TableHead>
                  <TableHead>Check ID</TableHead>
                  <TableHead>Employee</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Check Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned</TableHead>
                  <TableHead>TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getDrillDownData().data.map((check, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                    <TableCell className="font-mono text-xs text-blue-600">{check.check_id?.slice(-8) || check.id?.slice(-8)}</TableCell>
                    <TableCell className="text-sm">{check.employee_name}</TableCell>
                    <TableCell className="text-sm">{check.client_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{getCheckTypeLabel(check.check_type)}</Badge></TableCell>
                    <TableCell><Badge className={`${STATUS_COLORS[check.status] || 'bg-gray-100'} text-xs`}>{check.status?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-xs">{formatDate(check.assigned_at)}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {check.days_elapsed || 0}/{check.agent_tat || 7}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Transfer to Vendor Dialog */}
      <Dialog open={showBulkTransferDialog} onOpenChange={setShowBulkTransferDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-amber-600" />
              Transfer to Vendor Team
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>{selectedChecks.length}</strong> check(s) will be transferred to the Vendor Team for physical verification.
              </p>
            </div>
            <div>
              <Label>Select Vendor</Label>
              <Select value={transferData.vendor_id} onValueChange={(v) => setTransferData({...transferData, vendor_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason for Transfer</Label>
              <Select value={transferData.reason} onValueChange={(v) => setTransferData({...transferData, reason: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical_visit_required">Physical Visit Required</SelectItem>
                  <SelectItem value="document_collection">Document Collection Needed</SelectItem>
                  <SelectItem value="location_specific">Location-specific Verification</SelectItem>
                  <SelectItem value="candidate_unavailable">Candidate Unavailable for Phone</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Special Instructions to Vendor</Label>
              <Textarea
                placeholder="Enter any special instructions for the vendor (e.g., 'Find additional phone number', 'Verify alternate address')"
                value={transferData.special_instructions}
                onChange={(e) => setTransferData({...transferData, special_instructions: e.target.value})}
                className="min-h-[80px]"
              />
              <p className="text-xs text-gray-500 mt-1">These instructions will be visible to the vendor and field agent</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkTransferDialog(false)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleBulkTransferToVendor}>
              <ArrowRight className="w-4 h-4 mr-2" />
              Transfer {selectedChecks.length} Check(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Urgent TAT Reminder Dialog — May 22, 2026 */}
      <Dialog
        open={showUrgentReminderDialog}
        onOpenChange={(o) => {
          if (!o) { setShowUrgentReminderDialog(false); setUrgentReminderSingleCheck(null); setUrgentReminderResult(null); }
        }}
      >
        <DialogContent className="max-w-xl" data-testid="urgent-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Send className="w-5 h-5" />
              Send Urgent TAT Reminder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 text-red-900">
              <p className="font-semibold text-sm mb-1">⚠ This will email the assigned vendor(s) with a strong escalation banner:</p>
              <p className="text-xs italic">"Need below TAT crossed report urgently or face penalty"</p>
              <p className="text-xs mt-2 text-red-800">
                Use this only for TAT-crossed or TAT-closing checks. The email uses the same table format as the daily scheduled reminder; CC includes the admin team, the agent who assigned the check, and you.
              </p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p>
                Sending reminder for{' '}
                <strong>
                  {urgentReminderSingleCheck ? 1 : selectedChecks.length}
                </strong>{' '}
                check(s). The system will group by vendor — one email per vendor with all selected rows.
              </p>
            </div>
            {urgentReminderResult && (
              <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm" data-testid="urgent-reminder-result">
                <div className="font-semibold text-emerald-800">{urgentReminderResult.message}</div>
                {Array.isArray(urgentReminderResult.sent_breakdown) && urgentReminderResult.sent_breakdown.length > 0 && (
                  <ul className="mt-2 text-xs list-disc ml-5">
                    {urgentReminderResult.sent_breakdown.map((s, i) => (
                      <li key={i}>{s.vendor_name} ({s.vendor_email}) — {s.checks_count} check(s), {s.cc_count} CC</li>
                    ))}
                  </ul>
                )}
                {Array.isArray(urgentReminderResult.skipped) && urgentReminderResult.skipped.length > 0 && (
                  <div className="mt-2 text-xs text-amber-800">
                    Skipped: {urgentReminderResult.skipped.map((s) => `${s.case_id || s.vendor_id} (${s.reason})`).join(' · ')}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowUrgentReminderDialog(false); setUrgentReminderSingleCheck(null); }}
              disabled={urgentReminderSending}
              data-testid="urgent-reminder-cancel-btn"
            >
              {urgentReminderResult ? 'Close' : 'Cancel'}
            </Button>
            {!urgentReminderResult && (
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={urgentReminderSending}
                onClick={() => sendUrgentReminder(urgentReminderSingleCheck)}
                data-testid="urgent-reminder-confirm-send-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {urgentReminderSending ? 'Sending…' : 'Send Urgent Reminder'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
